import { buffers } from 'redux-saga'
import { put, select, call, takeEvery, all, takeLatest, race, take, delay, actionChannel } from 'redux-saga/effects'
import { fromJS, is, List } from 'immutable'
import { CustomTagsNewsTypes } from 'static/constants'

import * as AppActions from 'actions/app'
import * as Actions from 'actions/media_reviews'
import * as NewsActions from 'actions/news'
import { listUniqueById, listChunk } from 'utils/immutable'
import * as Api from 'api/bff'
import * as Selectors from 'selectors'
import { multiSelect } from 'utils/multiselect'
import { sortGroupedNews } from 'utils/sorting'
import {
  normalizeSnapshot,
  projectSortingToSnapshot,
  removeNewsFromSnapshot,
  snapshotMembershipMatches,
  snapshotMatchesRegroup
} from 'utils/media_review_snapshot'

export function* loadFullTextStart({ payload: news }) {
  const newsId = news.get('id')

  try {
    const response = yield call(Api.loadFullText, newsId)

    const payload = fromJS({
      newsId,
      text: response.news.text
    })
    yield put(Actions.loadFullTextSuccess(payload))
  } catch (error) {
    yield put(Actions.loadFullTextError(error))
  }
}

const isSelectedMediaReview = (selectedMediaReviewId, mediaReviewId) => (
  String(selectedMediaReviewId) === String(mediaReviewId)
)

const toNumericSorting = sorting => normalizeSnapshot(sorting).map(group => ({
  code_id: group.get('codeId') == null ? null : Number(group.get('codeId')),
  news_ids: group.get('newsIds').map(Number).toArray()
})).toArray()

export function* checkDiff({
  payload: {
    mediaReviewId,
    baseSnapshot,
    sorting,
    preconditionRetried
  }
}) {
  try {
    let selectedMediaReviewId = yield select(Selectors.getSelectedMediaReviewId)

    if (!isSelectedMediaReview(selectedMediaReviewId, mediaReviewId)) {
      return
    }

    const body = {
      id: mediaReviewId,
      sorting: toNumericSorting(sorting),
      base_snapshot: toNumericSorting(baseSnapshot)
    }

    yield put(Actions.reorderStart())
    const result = yield call(Api.reorderMediaReview, body)
    selectedMediaReviewId = yield select(Selectors.getSelectedMediaReviewId)

    if (isSelectedMediaReview(selectedMediaReviewId, mediaReviewId)) {
      const desiredSnapshot = projectSortingToSnapshot(sorting, [baseSnapshot])

      if (snapshotMembershipMatches(result.snapshot, desiredSnapshot)) {
        yield put(Actions.reconcileSorting(result.snapshot))
        yield put(Actions.setSnapshot(result.snapshot))

        if (!is(normalizeSnapshot(result.snapshot), desiredSnapshot)) {
          const i18n = yield select(Selectors.getI18n)

          yield put(AppActions.showAppSnackbarMessage({
            key: 'media-review-concurrent-changes',
            text: i18n.get('media_review_concurrent_changes_applied'),
            variant: 'info'
          }))
        }
      } else {
        const i18n = yield select(Selectors.getI18n)

        yield put(AppActions.showAppSnackbarMessage({
          key: 'media-review-concurrent-changes',
          loading: true,
          persist: true,
          text: i18n.get('media_review_concurrent_changes_loading'),
          variant: 'info'
        }))
        yield put(Actions.fetchNewsForMediaReviewStart({
          background: true,
          mediaReviewId,
          notifyConcurrentChanges: true
        }))
      }

      yield put(Actions.reorderSuccess())
      yield put(Actions.checkDiffSuccess())
    }
  } catch (error) {
    const selectedMediaReviewId = yield select(Selectors.getSelectedMediaReviewId)

    if (!isSelectedMediaReview(selectedMediaReviewId, mediaReviewId)) {
      return
    }

    if (error.response && error.response.statusCode === 409 && !preconditionRetried) {
      yield call(checkDiff, {
        payload: {
          mediaReviewId,
          baseSnapshot,
          sorting,
          preconditionRetried: true
        }
      })

      return
    }

    yield put(Actions.reorderError(error))
    yield put(Actions.checkDiffError(error))
    yield put(AppActions.exception(error))
    yield put(Actions.fetchNewsForMediaReviewStart(mediaReviewId))
  }
}

export function* fetchNewsForMediaReview({ payload }) {
  const options = payload && typeof payload === 'object'
    ? payload
    : { mediaReviewId: payload }
  const {
    mediaReviewId,
    notifyConcurrentChanges
  } = options

  try {
    yield put(Actions.selectMediaReview(mediaReviewId))

    const result = yield call(Api.getMediaReview, mediaReviewId)
    const selectedMediaReviewId = yield select(Selectors.getSelectedMediaReviewId)

    if (!isSelectedMediaReview(selectedMediaReviewId, mediaReviewId)) {
      if (notifyConcurrentChanges) {
        yield put(AppActions.hideAppSnackbarMessage())
      }

      return
    }

    const news = listUniqueById(fromJS(result.data).flatMap(d => d.get('news')))

    yield put(NewsActions.addNews(news.toJS()))
    yield put(Actions.fetchNewsForMediaReviewSuccess(result))

    if (notifyConcurrentChanges) {
      const i18n = yield select(Selectors.getI18n)

      yield put(AppActions.showAppSnackbarMessage({
        key: 'media-review-concurrent-changes',
        text: i18n.get('media_review_concurrent_changes_applied'),
        variant: 'info'
      }))
    }
  } catch (error) {
    yield put(Actions.fetchNewsForMediaReviewError(error))

    if (notifyConcurrentChanges) {
      yield put(AppActions.hideAppSnackbarMessage())
    }

    yield put(AppActions.genericErrorMessage())
    yield put(AppActions.exception(error))
  }
}

export function* queueLayoutSave() {
  const mediaReviewId = yield select(Selectors.getSelectedMediaReviewId)
  const baseSnapshot = yield select(Selectors.getMediaReviewDetailSnapshot)
  const sorting = yield select(Selectors.getMediaReviewSorting)

  yield put(Actions.checkDiffStart({ mediaReviewId, baseSnapshot, sorting }))
}

export function* moveCodeInMediaReview() {
  yield call(queueLayoutSave)
  yield put(Actions.moveCodeInMediaReviewSuccess())
}

export function* moveNewsInMediaReview() {
  yield call(queueLayoutSave)
  yield put(Actions.moveNewsInMediaReviewSuccess())
}

export function* removeNews({ payload: newsId }) {
  const mediaReviewId = yield select(Selectors.getSelectedMediaReviewId)
  const baseSnapshot = yield select(Selectors.getMediaReviewDetailSnapshot)
  const news = yield select(Selectors.getNewsById, newsId)
  const clusteredNewsIds = news
    ? news.get('clusteredNews', List()).map(clusteredNews => clusteredNews.get('id'))
    : List()

  yield put(Actions.checkDiffStart({
    command: 'removeNews',
    mediaReviewId,
    baseSnapshot,
    newsId,
    removedNewsIds: clusteredNewsIds.push(newsId)
  }))
}

export function* executeRemoveNews({
  mediaReviewId,
  baseSnapshot,
  newsId,
  removedNewsIds
}) {
  try {
    let selectedMediaReviewId = yield select(Selectors.getSelectedMediaReviewId)

    if (!isSelectedMediaReview(selectedMediaReviewId, mediaReviewId)) {
      return
    }

    const body = {
      id: newsId,
      media_review_id: mediaReviewId
    }

    const result = yield call(Api.removeNewsFromMediaReview, body)
    selectedMediaReviewId = yield select(Selectors.getSelectedMediaReviewId)

    if (!isSelectedMediaReview(selectedMediaReviewId, mediaReviewId)) {
      return
    }

    const expectedSnapshot = removeNewsFromSnapshot(baseSnapshot, [newsId])
    const savedSnapshot = normalizeSnapshot(result.snapshot)

    if (is(savedSnapshot, expectedSnapshot)) {
      yield put(Actions.setSnapshot(savedSnapshot))
    }

    yield put(Actions.removeNewsFromMediaReviewSuccess({
      newsId,
      newsIds: removedNewsIds
    }))
    yield put(Actions.updateMediaReviewStats())
  } catch (error) {
    yield put(Actions.removeNewsFromMediaReviewError(error))
  }
}

export function* regroupNews({ payload: news }) {
  const isMediaReviewDetail = yield select(Selectors.isMediaReviewDetail)

  if (isMediaReviewDetail) {
    const mediaReviewId = yield select(Selectors.getSelectedMediaReviewId)
    const baseSnapshot = yield select(Selectors.getMediaReviewDetailSnapshot)

    yield put(Actions.checkDiffStart({
      command: 'regroupNews',
      mediaReviewId,
      baseSnapshot,
      newsId: news.get('id'),
      codeIds: news.get('codes').map(code => code.get('id'))
    }))
  }
}

export function* reconcileRegroup({
  mediaReviewId,
  baseSnapshot,
  newsId,
  codeIds
}) {
  try {
    let selectedMediaReviewId = yield select(Selectors.getSelectedMediaReviewId)

    if (!isSelectedMediaReview(selectedMediaReviewId, mediaReviewId)) {
      return
    }

    const savedSnapshot = yield call(Api.getMediaReviewSnapshot, mediaReviewId)
    selectedMediaReviewId = yield select(Selectors.getSelectedMediaReviewId)

    if (
      isSelectedMediaReview(selectedMediaReviewId, mediaReviewId)
      && snapshotMatchesRegroup(baseSnapshot, savedSnapshot, newsId, codeIds)
    ) {
      yield put(Actions.setSnapshot(savedSnapshot))
    }
  } catch (error) {
    yield put(AppActions.exception(error))
  }

  yield put(Actions.updateMediaReviewStats())
}

export function* showAllSnippets() {
  const groupedNews = yield select(Selectors.getMediaReviewGroupedNews)
  const newsIds = groupedNews.flatMap(index => index.get('news').map(newsItem => fromJS({
    newsId: newsItem,
    codeId: index.getIn(['code', 'id'])
  })))
  yield put(Actions.showSnippet(newsIds))
}

export function* hideAllSnippets() {
  const groupedNews = yield select(Selectors.getMediaReviewGroupedNews)
  const newsIds = groupedNews.flatMap(index => index.get('news').map(newsItem => fromJS({
    newsId: newsItem,
    codeId: index.getIn(['code', 'id'])
  })))
  yield put(Actions.hideSnippet(newsIds))
}

export function* showAllSummaries() {
  const groupedNews = yield select(Selectors.getMediaReviewGroupedNews)
  const newsIds = groupedNews.flatMap(index => index.get('news').map(newsItem => fromJS({
    newsId: newsItem,
    codeId: index.getIn(['code', 'id'])
  })))
  yield put(Actions.showSummary(newsIds))
}

export function* hideAllSummaries() {
  const groupedNews = yield select(Selectors.getMediaReviewGroupedNews)
  const newsIds = groupedNews.flatMap(index => index.get('news').map(newsItem => fromJS({
    newsId: newsItem,
    codeId: index.getIn(['code', 'id'])
  })))
  yield put(Actions.hideSummary(newsIds))
}

export function* updateMediaReviewStats() {
  const mediaReviewId = yield select(Selectors.getSelectedMediaReviewId)
  const news = yield select(Selectors.getMediaReviewNewsUngrouped)

  yield put(Actions.recalculateMediaReviewStats({
    mediaReviewId,
    news
  }))
}

export function* shiftSelectNewsForSorting({ payload: { newsIds, isReplace, keyPress } }) {
  const codeId = newsIds.first().get('codeId')
  const newsByCodeId = yield select(Selectors.getMediaReviewNewsByCodeId, codeId)
  const selectedNewsForSorting = yield select(Selectors.getMediaReviewSelectedNewsForSorting)

  if (selectedNewsForSorting.isEmpty()) {
    yield put(Actions.selectNewsForSorting({ newsIds, isReplace, keyPress }))
  } else {
    const shiftSelection = yield call(multiSelect, selectedNewsForSorting, newsByCodeId, newsIds)
    yield put(Actions.selectNewsForSorting({ newsIds: shiftSelection, isReplace, keyPress }))
  }
}

export function* collapseAllTopics() {
  const codeIds = yield select(Selectors.getMediaReviewCodeIds)

  yield put(Actions.collapseTopics(codeIds))
}

export function* expandAllTopics() {
  const codeIds = yield select(Selectors.getMediaReviewCodeIds)

  yield put(Actions.expandTopics(codeIds))
}

export function* sortBy({ payload: { field, order } }) {
  const sortLast = 'zzzzzzzzzzzzz'
  let newGroupedNews

  if (field === 'topic') {
    const groupedNews = yield select(Selectors.getMediaReviewGroupedNews)
    newGroupedNews = groupedNews.sortBy(groupIndex => groupIndex.getIn(['code', 'sortcode']) || sortLast)

    if (order === 'desc') {
      newGroupedNews = newGroupedNews.reverse()
    }
  } else {
    const groupedNews = yield select(Selectors.getMediaReviewNews)

    const isPublicationField = ['publicationName', 'channelId'].indexOf(field) !== -1
    let pathToSortingField = [field]

    if (isPublicationField) {
      pathToSortingField = ['publication', field === 'publicationName' ? 'name' : field]
    }

    newGroupedNews = sortGroupedNews(groupedNews, pathToSortingField, order)
  }

  yield put(Actions.setGroupedNews(newGroupedNews))
}

const mapCodeNewsPairs = (codeId, newsIds) => {
  if (newsIds.isEmpty() && codeId !== undefined) {
    return fromJS([`${codeId}___empty__`])
  }

  return newsIds.map(n => (codeId === undefined ? String(n) : `${codeId}_${n}`))
}

export function* selectNews({ payload: { codeId, newsIds, omitIntersection } }) {
  let codeNewsIdPairs = mapCodeNewsPairs(codeId, newsIds)
  let selectedNews = yield select(Selectors.getMediaReviewSelectedNews)
  const newsClusterIds = yield select(Selectors.getNewsClusterIdsOfAllCodesInMediaReview)

  const allCodeNewsIdPairs = newsClusterIds
    .map(pair => mapCodeNewsPairs(pair.get('codeId'), pair.get('newsIds')))
    .flatten(true)

  if (selectedNews.isEmpty()) {
    codeNewsIdPairs = allCodeNewsIdPairs
      .filterNot(c => codeNewsIdPairs.includes(c))
  }

  yield put(Actions.setSelectedNews({ codeNewsIdPairs, omitIntersection }))

  selectedNews = yield select(Selectors.getMediaReviewSelectedNews)

  if (selectedNews.size === allCodeNewsIdPairs.size) {
    yield put(Actions.resetNewsSelection())
  }
}

export function* invertNewsSelection() {
  const selectedNews = yield select(Selectors.getMediaReviewSelectedNews)
  const newsClusterIds = yield select(Selectors.getNewsClusterIdsOfAllCodesInMediaReview)

  const allCodeNewsIdPairs = newsClusterIds
    .map(pair => mapCodeNewsPairs(pair.get('codeId'), pair.get('newsIds')))
    .flatten(true)

  const codeNewsIdPairs = allCodeNewsIdPairs.filterNot(c => selectedNews.includes(c))

  yield put(Actions.resetNewsSelection())
  yield put(Actions.setSelectedNews({ codeNewsIdPairs }))
}

export function* filterMediaReviewsStart() {
  const filterData = yield select(Selectors.getMediaReviewsFilter)

  try {
    const body = {
      media_review_type_ids: filterData.get('filter'),
      page: filterData.get('page') + 1,
      page_size: filterData.get('pageSize')
    }
    const dateFrom = filterData.get('dateFrom')
    const dateTo = filterData.get('dateTo')

    if (dateFrom) {
      body.date_from = dateFrom
    }

    if (dateTo) {
      body.date_to = dateTo
    }

    const result = yield call(Api.filterMediaReviews, body)

    yield put(Actions.filterMediaReviewsSuccess(result))
  } catch (error) {
    yield put(Actions.filterMediaReviewsError(error))
    yield put(AppActions.genericErrorMessage())
  }
}

export function* pinAll({ payload: { customTag, action } }) {
  try {
    const i18n = yield select(Selectors.getI18n)
    const moduleName = yield select(Selectors.getViewConfigModuleName)
    let news = yield select(Selectors.getMediaReviewsSelectedNewsObjects)

    if (news.isEmpty()) {
      news = yield select(Selectors.getMediaReviewNewsUngrouped)
    }

    news = news.toList()

    const newsIncludingClusters = news.reduce((acc, n) => {
      const cn = n.get('clusteredNews') || fromJS([])

      return acc.concat(cn.push(n))
    }, List())

    const chunkCount = Math.ceil(newsIncludingClusters.size / 100)
    const chunks = listChunk(newsIncludingClusters, chunkCount)

    yield put(AppActions.setAppBarMessage(i18n.get('pinning_progress', { progress: '0%' })))

    let changedCount = 0
    for (let i = 0; i < chunks.size; i += 1) {
      const chunkNews = chunks.get(i)

      let progress = `${Math.ceil(((i + 1) / chunks.size) * 100)}%`
      yield put(AppActions.setAppBarMessage(i18n.get('pinning_progress', { progress })))

      const data = chunkNews.map(n => (fromJS({
        news_id: n.get('id'),
        article_date: n.get('articleDate'),
        [`${action === 'unpin' ? '-' : '+'}custom_tag_ids`]: [customTag.get('id')]
      })))

      yield call(Api.assignCustomTagToNews, { data, news_type: CustomTagsNewsTypes[moduleName] })

      changedCount += data.size

      progress = `${Math.ceil(((i + 1) / chunks.size) * 100)}%`
      yield put(AppActions.setAppBarMessage(i18n.get('pinning_progress', { progress })))
    }

    const newTag = customTag.update('newsCount', count => count + (action === 'unpin' ? -changedCount : changedCount))

    news = news.map(n => n.update('customTags', customTags => {
      if (action === 'unpin') {
        return customTags.filter(c => c.get('id') !== newTag.get('id'))
      }

      return customTags.push(newTag)
    }))

    yield put(NewsActions.updateNews(news))
    yield put(AppActions.setAppBarMessage(null))
    yield put(Actions.pinAllSuccess({ customTag, pinnedCount: news.size }))
    yield put(AppActions.genericSuccessMessage())
  } catch (error) {
    yield put(AppActions.setAppBarMessage(null))
    yield put(Actions.pinAllError(error))
    yield put(AppActions.genericErrorMessage())
    yield put(AppActions.exception(error))
  }
}

export function* unpinAll({ payload: { customTag } }) {
  yield call(pinAll, { payload: { customTag, action: 'unpin' } })
}

export function* watchFetchNewsForMediaReview() {
  yield takeEvery(Actions.fetchNewsForMediaReviewStart, fetchNewsForMediaReview)
}

export function* watchMoveCode() {
  yield takeEvery(Actions.moveCodeInMediaReviewStart, moveCodeInMediaReview)
}

export function* watchMoveNews() {
  yield takeEvery(Actions.moveNewsInMediaReviewStart, moveNewsInMediaReview)
}

export function* watchRemoveNews() {
  yield takeEvery(Actions.removeNewsFromMediaReviewStart, removeNews)
}

export function* watchRegroupNews() {
  yield takeEvery(Actions.regroupNews, regroupNews)
}

export function* watchUpdateMediaReviewStats() {
  yield takeEvery(Actions.updateMediaReviewStats, updateMediaReviewStats)
}

export function* watchCollapseAllTopics() {
  yield takeEvery(Actions.collapseAllTopics, collapseAllTopics)
}

export function* watchExpandAllTopics() {
  yield takeEvery(Actions.expandAllTopics, expandAllTopics)
}

export function* watchShowAllSnippets() {
  yield takeEvery(Actions.showAllSnippets, showAllSnippets)
}

export function* watchHideAllSnippets() {
  yield takeEvery(Actions.hideAllSnippets, hideAllSnippets)
}

export function* watchShowAllSummaries() {
  yield takeEvery(Actions.showAllSummaries, showAllSummaries)
}

export function* watchHideAllSummaries() {
  yield takeEvery(Actions.hideAllSummaries, hideAllSummaries)
}

export function* watchShiftSelectNewsForSorting() {
  yield takeEvery(Actions.shiftSelectNewsForSorting, shiftSelectNewsForSorting)
}

export function* watchLoadFullTextStart() {
  yield takeEvery(Actions.loadFullTextStart, loadFullTextStart)
}

export function* watchSortBy() {
  yield takeEvery(Actions.sortBy, sortBy)
}

export function* watchSelectNews() {
  yield takeEvery(Actions.selectNews, selectNews)
}

export function* watchInvertNewsSelection() {
  yield takeEvery(Actions.invertNewsSelection, invertNewsSelection)
}

export function* watchFilterMediaReviewsStart() {
  yield takeLatest(Actions.filterMediaReviewsStart, filterMediaReviewsStart)
}

export function* watchPinAll() {
  yield takeEvery(Actions.pinAllStart, pinAll)
}

export function* watchUnpinAll() {
  yield takeEvery(Actions.unpinAll, unpinAll)
}

export function* takeLatestLayoutSave(channel, pendingAction) {
  let action = pendingAction || (yield take(channel))

  if (action.payload.command) {
    return { action, pendingAction: null }
  }

  for (; ;) {
    const { nextAction } = yield race({
      nextAction: take(channel),
      timeout: delay(1000)
    })

    if (!nextAction) {
      return { action, pendingAction: null }
    }

    if (nextAction.payload.command) {
      return { action, pendingAction: nextAction }
    }

    action = nextAction
  }
}

export function* processLayoutCommand(action) {
  switch (action.payload.command) {
    case 'removeNews':
      yield call(executeRemoveNews, action.payload)
      break
    case 'regroupNews':
      yield call(reconcileRegroup, action.payload)
      break
    default:
      yield call(checkDiff, action)
  }
}

export const rebaseLayoutCommand = (action, selectedMediaReviewId, confirmedSnapshot) => {
  if (!isSelectedMediaReview(selectedMediaReviewId, action.payload.mediaReviewId)) {
    return action
  }

  return {
    ...action,
    payload: {
      ...action.payload,
      baseSnapshot: confirmedSnapshot
    }
  }
}

export function* watchCheckDiff() {
  const channel = yield actionChannel(Actions.checkDiffStart, buffers.expanding())
  let pendingAction = null

  for (; ;) {
    const result = yield call(takeLatestLayoutSave, channel, pendingAction)

    pendingAction = result.pendingAction
    const selectedMediaReviewId = yield select(Selectors.getSelectedMediaReviewId)
    const confirmedSnapshot = yield select(Selectors.getMediaReviewDetailSnapshot)
    const action = rebaseLayoutCommand(result.action, selectedMediaReviewId, confirmedSnapshot)

    yield call(processLayoutCommand, action)
  }
}

export default function* mediaReviewSaga() {
  yield all([
    watchFetchNewsForMediaReview(),
    watchMoveCode(),
    watchMoveNews(),
    watchRemoveNews(),
    watchRegroupNews(),
    watchUpdateMediaReviewStats(),
    watchCollapseAllTopics(),
    watchExpandAllTopics(),
    watchShowAllSnippets(),
    watchHideAllSnippets(),
    watchShowAllSummaries(),
    watchHideAllSummaries(),
    watchShiftSelectNewsForSorting(),
    watchLoadFullTextStart(),
    watchSortBy(),
    watchSelectNews(),
    watchInvertNewsSelection(),
    watchFilterMediaReviewsStart(),
    watchPinAll(),
    watchUnpinAll(),
    watchCheckDiff()
  ])
}
